/**
 * Fiche Google Maps de l'entreprise : lien direct, note et nombre d'avis.
 *
 * Le commercial les renseigne depuis la fiche (il a Google sous les yeux) ou
 * l'enrichissement les apporte via l'API Places. Le lien reste la référence :
 * un clic ouvre la fiche Google pour vérifier.
 */
import { useState } from "react";
import { toast } from "sonner";
import { MapPin, Star, Pencil, ExternalLink, Loader2 } from "lucide-react";
import api, { apiError } from "@/lib/crmApi";

const FIELD = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-indigo-500";

export function Stars({ rating }) {
  if (rating === null || rating === undefined || rating === "") return null;
  const r = Number(rating);
  return (
    <span className="inline-flex items-center gap-0.5" aria-label={`${r} sur 5`} data-testid="google-stars">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} size={13} className={n <= Math.round(r) ? "fill-amber-400 text-amber-400" : "text-slate-200"} />
      ))}
    </span>
  );
}

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR") : "");

export default function GoogleMapsCard({ company, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ google_maps_url: "", google_rating: "", google_reviews_count: "" });
  const [saving, setSaving] = useState(false);
  const has = company.google_maps_url || company.google_rating != null || company.google_reviews_count != null;

  const open = () => {
    setForm({
      google_maps_url: company.google_maps_url || "",
      google_rating: company.google_rating ?? "",
      google_reviews_count: company.google_reviews_count ?? "",
    });
    setEditing(true);
  };
  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/companies/${company.id}/google`, form);
      toast.success("Fiche Google enregistrée");
      setEditing(false);
      onSaved?.();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Enregistrement impossible"); }
    finally { setSaving(false); }
  };

  if (editing) {
    return (
      <div className="mt-3 rounded-xl border border-indigo-200 bg-indigo-50/40 p-3" data-testid="google-editor">
        <div className="mb-2 font-mono text-[10.5px] uppercase tracking-[.1em] text-slate-500">Fiche Google Maps</div>
        <label className="block text-xs text-slate-500">Lien Google Maps
          <input data-testid="google-url" value={form.google_maps_url} placeholder="https://maps.app.goo.gl/… ou google.com/maps/place/…"
            onChange={(e) => setForm({ ...form, google_maps_url: e.target.value })} className={`${FIELD} mt-1 font-mono text-[12.5px]`} autoFocus />
        </label>
        <div className="mt-2 grid grid-cols-2 gap-2">
          <label className="block text-xs text-slate-500">Note (sur 5)
            <input data-testid="google-rating" type="number" step="0.1" min="0" max="5" value={form.google_rating} placeholder="4,6"
              onChange={(e) => setForm({ ...form, google_rating: e.target.value })} className={`${FIELD} mt-1`} />
          </label>
          <label className="block text-xs text-slate-500">Nombre d'avis
            <input data-testid="google-reviews" type="number" min="0" value={form.google_reviews_count} placeholder="128"
              onChange={(e) => setForm({ ...form, google_reviews_count: e.target.value })} className={`${FIELD} mt-1`} />
          </label>
        </div>
        <p className="mt-2 text-[11.5px] text-slate-400">Laisser un champ vide l'efface. Le lien doit pointer vers Google Maps.</p>
        <div className="mt-2.5 flex gap-2">
          <button data-testid="google-save" disabled={saving} onClick={save}
            className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50">
            {saving && <Loader2 size={13} className="animate-spin" />} Enregistrer
          </button>
          <button onClick={() => setEditing(false)} className="rounded-lg px-3 py-1.5 text-sm text-slate-600 hover:bg-white">Annuler</button>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl border border-slate-100 bg-slate-50/60 px-3 py-2" data-testid="google-card">
      <MapPin size={13} className="text-rose-500" />
      {company.google_rating != null && company.google_rating !== "" ? (
        <span className="flex items-center gap-1.5 text-[13px] text-slate-800" data-testid="google-rating-display">
          <b>{Number(company.google_rating).toLocaleString("fr-FR", { maximumFractionDigits: 1 })}</b>
          <Stars rating={company.google_rating} />
          {company.google_reviews_count != null && company.google_reviews_count !== "" && (
            <span className="text-slate-500">({company.google_reviews_count} avis)</span>
          )}
        </span>
      ) : (
        <span className="text-[12.5px] text-slate-400">{has ? "Note Google non renseignée" : "Fiche Google Maps non renseignée"}</span>
      )}
      {company.google_maps_url && (
        <a href={company.google_maps_url} target="_blank" rel="noreferrer" data-testid="google-link"
          className="inline-flex items-center gap-1 text-[12.5px] text-indigo-600 hover:underline">
          Voir la fiche Google Maps <ExternalLink size={11} />
        </a>
      )}
      {company.google_checked_at && (
        <span className="text-[11px] text-slate-400" title={company.google_checked_by_name ? `par ${company.google_checked_by_name}` : ""}>
          vérifié le {fmtDate(company.google_checked_at)}
        </span>
      )}
      <button data-testid="google-edit" onClick={open} title="Renseigner le lien, la note et les avis"
        className="ml-auto inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-[12px] text-slate-500 hover:bg-white hover:text-slate-900">
        <Pencil size={12} /> {has ? "Modifier" : "Renseigner"}
      </button>
    </div>
  );
}
